/**
 * @fileoverview Organization service: tolerant /orgs parsing with /auth/me fallback.
 * @module features/orgs/api/orgsService
 */

import apiClient from '@/lib/apiClient'
import {
  AuthMeSchema,
  OrgsArraySchema,
  OrgsEnvelopeContent,
  OrgsEnvelopeData,
  OrgsEnvelopeOrganizations,
  type Org,
  type Profile,
  ProfileSchema,
  OrgSchema,
  OrgSettingsSchema,
  type OrgSettings,
} from '../schemas/orgSchemas'

/**
 * Attempt to normalize arbitrary response shapes into Org[].
 * Accepts [] | { organizations } | { content } | { data }.
 * @param payload - Raw response body
 */
function normalizeOrgs(payload: unknown): Org[] | null {
  const arrayParsed = OrgsArraySchema.safeParse(payload)
  if (arrayParsed.success) return arrayParsed.data

  const orgsParsed = OrgsEnvelopeOrganizations.safeParse(payload)
  if (orgsParsed.success) return orgsParsed.data.organizations

  const contentParsed = OrgsEnvelopeContent.safeParse(payload)
  if (contentParsed.success) return contentParsed.data.content

  const dataParsed = OrgsEnvelopeData.safeParse(payload)
  if (dataParsed.success) return dataParsed.data.data

  return null
}

/**
 * Fetch enriched profile from /auth/me.
 */
export async function getProfile(): Promise<Profile> {
  const me = await apiClient.get('/auth/me')
  return ProfileSchema.parse(me.data)
}

/**
 * List organizations for current user with tolerant parsing and /auth/me fallback.
 * @returns Promise resolving to Org[]
 */
export async function listOrgs(): Promise<Org[]> {
  // Run /orgs and /auth/me in parallel and return the first valid normalization.
  const fromOrgs = (async () => {
    const res = await apiClient.get('/orgs')
    const normalized = normalizeOrgs(res.data)
    if (!normalized) throw new Error('Invalid /orgs shape')
    return normalized
  })()

  const fromMe = (async () => {
    const me = await apiClient.get('/auth/me')
    const parsed = AuthMeSchema.parse(me.data)
    return parsed.organizations
  })()

  // Resolve on first success; fall back to the other if one fails
  const results = await Promise.allSettled([fromOrgs, fromMe])
  for (const r of results) {
    if (r.status === 'fulfilled') return r.value
  }
  // Both failed: throw first reason
  throw (results[0] as PromiseRejectedResult).reason
}

/**
 * Create a new organization and return its summary (id, name, role).
 * @param name Organization display name
 * @returns Created organization object
 */
export async function createOrg(name: string): Promise<Org> {
  const res = await apiClient.post('/orgs', { name })
  // Backend returns { id, name, role }
  return OrgSchema.parse(res.data)
}

/**
 * Get organization settings (currency, etc).
 * @param orgId Organization ID
 */
export async function getOrgSettings(orgId: string): Promise<OrgSettings> {
  const res = await apiClient.get(`/orgs/${orgId}/settings`)
  return OrgSettingsSchema.parse(res.data)
}
